import React__default, { useState, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from './../external_lib/classnames/index.js';
import Dialog from './Dialog.js';
import { getStorePopupLabels } from '../Components/Molecules/FulfillmentSelector/Web/translations.js';
import OrderNowLater from '../Cells/OrderNowLater.js';
import { useIsMobile } from '../Utils/isMobile.util.js';
import SelectorContent from '../Components/Molecules/FulfillmentSelector/Web/selector.component.js';
import LocationDialogContent from '../Components/Molecules/FulfillmentSelector/Web/location.component.js';
import StoreDialogContent from '../Components/Molecules/FulfillmentSelector/Web/store.component.js';
import { FulfillmentType } from '../Enums/fulfillment_type.enum.js';
import SearchLocation from '../Components/Molecules/FulfillmentSelector/Web/search.component.js';

var DIALOG_STATES;
(function (DIALOG_STATES) {
    DIALOG_STATES[DIALOG_STATES["LOCATION_SEARCH"] = 0] = "LOCATION_SEARCH";
    DIALOG_STATES[DIALOG_STATES["STORE_SELECTION"] = 1] = "STORE_SELECTION";
    DIALOG_STATES[DIALOG_STATES["ORDER_NOW_LATER"] = 2] = "ORDER_NOW_LATER";
    DIALOG_STATES[DIALOG_STATES["DONE"] = 3] = "DONE";
})(DIALOG_STATES || (DIALOG_STATES = {}));
function FulfillmentSelector(_a) {
    var _b = _a.isOpen, isOpen = _b === void 0 ? false : _b, 
    /** fulfillment types */
    fulfillmentTypes = _a.fulfillmentTypes, initialFulfillmentType = _a.fulfillmentType, classes = _a.classes, 
    // google API Key
    googleApiKey = _a.googleApiKey, 
    /** returned stores by search */
    _c = _a.stores, 
    /** returned stores by search */
    stores = _c === void 0 ? [] : _c, 
    /** callback on store selected */
    onStoreSelected = _a.onStoreSelected, 
    /** callback on location selected */
    onLocationSelected = _a.onLocationSelected, 
    /** past locations */
    pastLocations = _a.pastLocations, 
    /** user locations */
    userLocations = _a.userLocations, 
    /** if delivery now/later dropdown needs to be shown */
    upFrontDeliveryNowLater = _a.upFrontDeliveryNowLater, 
    /** callback on close */
    onClose = _a.onClose, 
    /** when api calling happens */
    loading = _a.loading, 
    /** alreadu selected store and location */
    selectedStore = _a.selectedStore, 
    /** order now and later info */
    orderLaterData = _a.orderLaterData, startDate = _a.startDate, endDate = _a.endDate, 
    /** fetch the store timings */
    onOrderNowLaterRequested = _a.onOrderNowLaterRequested, isOrderNowDisabled = _a.isOrderNowDisabled, isOrderLaterDisabled = _a.isOrderLaterDisabled, storeTimings = _a.storeTimings, onLocationStore = _a.onLocationStore, searchMode = _a.searchMode;
    /** if no FF modes found, don't render anything */
    if (fulfillmentTypes.length === 0)
        return React__default.createElement(React__default.Fragment, null);
    var _d = useState(initialFulfillmentType || fulfillmentTypes[0]), fulfillmentType = _d[0], setFulfillmentType = _d[1];
    var t = useTranslation().t;
    var isMobile = useIsMobile();
    /** reference of FF selector */
    var parentRef = useRef(null);
    /** selected location from Google */
    var _e = useState(null), selectedLocation = _e[0], setSelectedLocation = _e[1];
    /** stores indexs from outside */
    var _f = useState((selectedStore === null || selectedStore === void 0 ? void 0 : selectedStore.id) || null), selectedStoreId = _f[0], setSelectedStoreId = _f[1];
    /** dialog state handler */
    var _g = useState(false), showDialog = _g[0], setShowDialog = _g[1];
    var handleOnClick = function () { return setShowDialog(true); };
    /** dialog states */
    var _h = useState(DIALOG_STATES.LOCATION_SEARCH), dialogState = _h[0], setDialogState = _h[1];
    /** dialog references */
    var _j = useState(''), dialogTitle = _j[0], setDialogTitle = _j[1];
    var _k = useState(''), dialogSubTitle = _k[0], setDialogSubTitle = _k[1];
    var _l = useState(false), removeCloseIcon = _l[0], setRemoveCloseIcon = _l[1];
    var _m = useState(), deliveryInfo = _m[0], setDeliveryInfo = _m[1];
    var getOrderNowOffsetTime = function (storeId, ffType) {
        var storeInfo = stores === null || stores === void 0 ? void 0 : stores.find(function (_store) { return _store.biz_location_id === storeId; });
        if (ffType === FulfillmentType.delivery) {
            return (storeInfo === null || storeInfo === void 0 ? void 0 : storeInfo.delivery_min_offset_time) / 60000;
        }
        if (ffType === FulfillmentType.pickup) {
            return (storeInfo === null || storeInfo === void 0 ? void 0 : storeInfo.pickup_min_offset_time) / 60000;
        }
    };
    /** reference dialog */
    var getDialogRef = useMemo(function () {
        if (parentRef.current &&
            !isMobile &&
            dialogState === DIALOG_STATES.LOCATION_SEARCH) {
            return parentRef.current;
        }
        return null;
    }, [dialogState, parentRef.current, isMobile]);
    var removeBackground = function () {
        return !isMobile && dialogState === DIALOG_STATES.LOCATION_SEARCH;
    };
    useEffect(function () {
        if (isOpen) {
            setShowDialog(true);
        }
    }, [isOpen]);
    /** set dialog title based on states */
    useEffect(function () {
        switch (dialogState) {
            case DIALOG_STATES.LOCATION_SEARCH:
                setSelectedStoreId(null);
                setDialogTitle(isMobile ? t('locationSearch.title') : '');
                setDialogSubTitle('');
                setRemoveCloseIcon(!isMobile); // remove icon on search desktop
                break;
            case DIALOG_STATES.STORE_SELECTION:
                if (stores.length === 1) {
                    return setDialogState(DIALOG_STATES.ORDER_NOW_LATER);
                }
                setDialogTitle(getStorePopupLabels(t).title);
                setDialogSubTitle(getStorePopupLabels(t).subtitle(stores.length));
                setRemoveCloseIcon(false);
                break;
            case DIALOG_STATES.ORDER_NOW_LATER:
                if (!upFrontDeliveryNowLater) {
                    return finializeDialog();
                }
                onOrderNowLaterRequested && onOrderNowLaterRequested(selectedStoreId);
                var store = stores.find(function (store) { return store.id === selectedStoreId; });
                setDialogTitle((store === null || store === void 0 ? void 0 : store.name) || '');
                setDialogSubTitle((store === null || store === void 0 ? void 0 : store.address) || '');
                setRemoveCloseIcon(false);
                break;
            default:
                finializeDialog();
                break;
        }
    }, [dialogState, isMobile, stores]);
    /** change dialog on stores passed */
    useEffect(function () {
        if (!loading && stores.length > 0) {
            if (stores.length === 1) {
                setSelectedStoreId(stores[0].id);
                setDialogState(DIALOG_STATES.ORDER_NOW_LATER);
            }
            else if (stores.length > 1 && selectedStoreId === null) {
                setDialogState(DIALOG_STATES.STORE_SELECTION);
            }
            else if (selectedStoreId !== null) {
                setDialogState(DIALOG_STATES.ORDER_NOW_LATER);
            }
        }
    }, [stores, loading, selectedStoreId]);
    /** closing dialog finalizing */
    var finializeDialog = function () {
        onClose && onClose();
        setShowDialog(false);
        setDialogState(DIALOG_STATES.LOCATION_SEARCH);
        if (fulfillmentType === 'pickup') {
            var selectedStore_1 = stores.find(function (store) { return store.id === selectedStoreId; });
            if (selectedStore_1 && selectedLocation) {
                selectedLocation.address = selectedStore_1 === null || selectedStore_1 === void 0 ? void 0 : selectedStore_1.address;
            }
        }
        if (selectedStoreId !== null) {
            if (upFrontDeliveryNowLater && deliveryInfo) {
                onStoreSelected(selectedStoreId, selectedLocation, fulfillmentType, deliveryInfo);
            }
            else if (!upFrontDeliveryNowLater) {
                onStoreSelected(selectedStoreId, selectedLocation, fulfillmentType);
            }
        }
    };
    var handleBackFromOrderNowLater = function () {
        if (dialogState === DIALOG_STATES.ORDER_NOW_LATER) {
            setDialogState(DIALOG_STATES.STORE_SELECTION);
        }
    };
    return (React__default.createElement(React__default.Fragment, null,
        searchMode ? (React__default.createElement(SearchLocation, { address: selectedStore === null || selectedStore === void 0 ? void 0 : selectedStore.address, handleOnClick: handleOnClick, parentRef: parentRef })) : (React__default.createElement(SelectorContent, { handleOnClick: handleOnClick, classes: classes, fulfillmentType: (selectedStore === null || selectedStore === void 0 ? void 0 : selectedStore.ffMode) || initialFulfillmentType, parentRef: parentRef, address: selectedStore === null || selectedStore === void 0 ? void 0 : selectedStore.address })),
        React__default.createElement(Dialog, { isOpen: showDialog, hasBack: dialogState === DIALOG_STATES.ORDER_NOW_LATER && stores.length > 1, onBack: function () { return handleBackFromOrderNowLater(); }, footer: false, onClose: finializeDialog, title: dialogTitle, subTitle: dialogSubTitle, removeCloseIcon: removeCloseIcon, classes: classNames('fulfillment-selector__dialog', {
                'fulfillment-selector__dialog_stretch': DIALOG_STATES.ORDER_NOW_LATER === dialogState ||
                    DIALOG_STATES.STORE_SELECTION === dialogState
            }), dialogRef: getDialogRef, removeBackdrop: removeBackground() },
            loading && React__default.createElement("div", { className: "loading-screen" }),
            dialogState === DIALOG_STATES.LOCATION_SEARCH && (React__default.createElement(LocationDialogContent, { googleApiKey: googleApiKey, onLocationChange: function () {
                    var args = [];
                    for (var _i = 0; _i < arguments.length; _i++) {
                        args[_i] = arguments[_i];
                    }
                    var placeId = args[0], latitude = args[1], longitude = args[2], fulfillmentType = args[3], address = args[4];
                    setSelectedLocation({
                        placeId: placeId,
                        latitude: latitude,
                        longitude: longitude,
                        address: address
                    });
                    onLocationSelected({
                        placeId: placeId,
                        latitude: latitude,
                        longitude: longitude,
                        address: address
                    }, fulfillmentType);
                    setFulfillmentType(fulfillmentType);
                }, fulfillmentType: fulfillmentType, pastLocations: pastLocations, userLocations: userLocations, fulfillmentTypes: fulfillmentTypes, onLocationStore: onLocationStore })),
            dialogState === DIALOG_STATES.STORE_SELECTION && (React__default.createElement(StoreDialogContent, { stores: stores, onStoreSelected: setSelectedStoreId })),
            dialogState === DIALOG_STATES.ORDER_NOW_LATER && (React__default.createElement(OrderNowLater, { orderNowOffsetTime: getOrderNowOffsetTime(selectedStoreId || 0, fulfillmentType) || 0, classes: "fullfillment-selector__order-now-later", orderLaterData: orderLaterData, variant: fulfillmentType, startDate: startDate, endDate: endDate, onContinue: function (args) {
                    setDeliveryInfo(args);
                    setDialogState(DIALOG_STATES.DONE);
                }, isOrderLaterDisabled: isOrderLaterDisabled, isOrderNowDisabled: isOrderNowDisabled, storeTimings: storeTimings })))));
}

export { FulfillmentSelector as default };
//# sourceMappingURL=FulfillmentSelector.js.map
